import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { getArticle, articles } from '@/data/articles';
import type { ArticleLocalized } from '@/data/articles';
import { MAIN_SITE_URL } from '@/mocks/categories';
import JsonLd from '@/components/base/JsonLd';
import { articleSchema } from '@/data/schema';

function resolveLocalized(article: { en: ArticleLocalized; pl: ArticleLocalized; es: ArticleLocalized; fr: ArticleLocalized }, lang: string): ArticleLocalized {
  const code = (lang || 'en').split('-')[0];
  if (code === 'pl') return article.pl;
  if (code === 'es') return article.es;
  if (code === 'fr') return article.fr;
  return article.en;
}

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = slug ? getArticle(slug) : undefined;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <main className="mx-auto max-w-3xl px-4 py-16 text-center md:px-6">
          <h1 className="text-3xl font-bold text-foreground-950">{t('article_not_found')}</h1>
          <p className="mt-4 text-foreground-600">{t('article_not_found_desc')}</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
          >
            {t('article_cta_secondary')}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const localized = resolveLocalized(article, i18n.language);

  const schema = articleSchema({
    slug: article.slug,
    title: localized.title,
    description: localized.lead,
    image: article.image,
    category: localized.category,
  });

  const related = articles
    .filter((a) => a.slug !== article.slug)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={schema} />
      <Header />
      <main>
        <div className="mx-auto max-w-3xl px-4 py-8 md:px-6 md:py-12">
          {/* Breadcrumb */}
          <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-700 cursor-pointer">
              {t('article_breadcrumb_home')}
            </Link>
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm"></i>
            </span>
            <span className="text-foreground-700">{localized.category}</span>
          </nav>

          {/* Article header */}
          <div className="mt-6">
            <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500">
              <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
                {localized.category}
              </span>
              <span className="flex items-center gap-1">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-time-line text-sm"></i>
                </span>
                {localized.readingTime}
              </span>
              <span className="flex items-center gap-1">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-calendar-line text-sm"></i>
                </span>
                {t('article_updated')} · 2026
              </span>
            </div>

            <h1 className="mt-5 text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
              {localized.title}
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-foreground-600">{localized.lead}</p>

            <div className="mt-6 overflow-hidden rounded-xl">
              <img
                src={article.image}
                alt={localized.title}
                title={localized.title}
                className="h-56 w-full object-cover object-top md:h-72"
              />
            </div>
          </div>

          {/* Sections */}
          {localized.sections.map((section, index) => (
            <section key={index} className="mt-10">
              <h2 className="text-2xl font-bold text-foreground-950">{section.h2}</h2>
              {section.paragraphs.map((p, pIndex) => (
                <p key={pIndex} className="mt-4 text-base leading-relaxed text-foreground-700">
                  {p}
                </p>
              ))}
              {section.list && (
                <ul className="mt-4 space-y-2">
                  {section.list.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-base text-foreground-700">
                      <span className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center text-primary-500">
                        <i className="ri-check-double-line text-sm"></i>
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          {/* Comparison table */}
          <section className="mt-10">
            <h2 className="text-2xl font-bold text-foreground-950">{localized.comparisonTitle}</h2>
            <div className="mt-5 overflow-x-auto rounded-lg border border-background-200 bg-white">
              <table className="w-full min-w-[520px] text-left text-sm">
                <thead>
                  <tr className="border-b border-background-200 bg-background-100">
                    <th className="px-5 py-3 font-semibold text-foreground-900">
                      {localized.comparisonHeaders[0]}
                    </th>
                    <th className="px-5 py-3 font-semibold text-primary-700">
                      {localized.comparisonHeaders[1]}
                    </th>
                    <th className="px-5 py-3 font-semibold text-secondary-700">
                      {localized.comparisonHeaders[2]}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {localized.comparisonRows.map((row, rowIndex) => (
                    <tr key={rowIndex} className={rowIndex < localized.comparisonRows.length - 1 ? 'border-b border-background-100' : ''}>
                      <td className="px-5 py-3 font-medium text-foreground-700">{row.label}</td>
                      <td className="px-5 py-3 text-foreground-600">{row.a}</td>
                      <td className="px-5 py-3 text-foreground-600">{row.b}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* FAQ */}
          <section className="mt-10">
            <h2 className="text-2xl font-bold text-foreground-950">{t('article_h2_6')}</h2>
            <div className="mt-5 space-y-3">
              {localized.faqs.map((faq) => (
                <div key={faq.q} className="rounded-lg border border-background-200 bg-white p-5">
                  <h3 className="text-base font-semibold text-foreground-900">{faq.q}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Internal links */}
          <div className="mt-10 rounded-lg border border-background-200 bg-background-100 p-5">
            <p className="text-sm font-semibold text-foreground-900">{t('article_cta_title')}</p>
            <p className="mt-1 text-sm text-foreground-600">
              <Link to="/#categories" className="font-medium text-primary-600 hover:underline cursor-pointer">
                {t('nav_browseCategories')}
              </Link>
              {' · '}
              <Link to="/#safety" className="font-medium text-primary-600 hover:underline cursor-pointer">
                {t('nav_safety')}
              </Link>
              {' · '}
              <Link to="/#faq" className="font-medium text-primary-600 hover:underline cursor-pointer">
                {t('nav_faq')}
              </Link>
            </p>
          </div>

          {/* Related articles */}
          <div className="mt-10">
            <h2 className="text-xl font-bold text-foreground-950">{t('article_related')}</h2>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {related.map((item) => (
                <Link
                  key={item.slug}
                  to={`/article/${item.slug}`}
                  className="group rounded-lg border border-background-200 bg-white p-4 transition-transform hover:-translate-y-1 cursor-pointer"
                >
                  <p className="text-sm font-semibold text-foreground-900 group-hover:text-primary-700">
                    {resolveLocalized(item, i18n.language).title}
                  </p>
                </Link>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-10 flex flex-col gap-3 rounded-lg bg-primary-500 p-8 text-center sm:flex-row sm:items-center sm:justify-center sm:gap-4">
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-base font-semibold text-primary-700 transition-colors hover:bg-background-100 cursor-pointer whitespace-nowrap"
            >
              {t('article_cta_primary')}
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-arrow-right-up-line text-sm"></i>
              </span>
            </a>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/60 px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-white/10 cursor-pointer whitespace-nowrap"
            >
              {t('article_cta_secondary')}
            </Link>
          </div>

          {/* Disclaimer */}
          <p className="mt-8 rounded-lg border border-background-200 bg-background-100 p-4 text-xs leading-relaxed text-foreground-500">
            {t('article_disclaimer')}
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}